import { useState } from "react"
import AngledSolarSystem from "@/components/demo/AngledSolarSystem"
import SolarSystemIllustration from "@/components/demo/SolarSystemIllustration"

const DemoPage = () => {
  // Regular version defaults
  const [centerDotRadius, setCenterDotRadius] = useState(20)
  const [ringSpacing, setRingSpacing] = useState(20)
  const [numberOfRings, setNumberOfRings] = useState(4)
  const [dotsPerRing, setDotsPerRing] = useState(6)
  const [useOutlines, setUseOutlines] = useState(true)
  const [dotSize, setDotSize] = useState(5)
  const [initialRingRadius, setInitialRingRadius] = useState(37)

  // Angled version defaults (from image)
  const [angledCenterDotRadius, setAngledCenterDotRadius] = useState(7)
  const [angledRingSpacing, setAngledRingSpacing] = useState(37)
  const [angledNumberOfRings, setAngledNumberOfRings] = useState(4)
  const [angledDotsPerRing, setAngledDotsPerRing] = useState(6)
  const [angledUseOutlines, setAngledUseOutlines] = useState(true)
  const [angledDotSize, setAngledDotSize] = useState(2)
  const [angledInitialRingRadius, setAngledInitialRingRadius] = useState(37)
  const [angledVerticalOffset, setAngledVerticalOffset] = useState(8)

  return (
    <div className="min-h-screen w-full bg-white">
      {/* Controls Panel */}
      <div className="w-full border-b border-neutral-200 bg-neutral-50 p-6">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-2xl font-semibold text-neutral-900 mb-6">
            Solar System Illustration Demo
          </h1>

          {/* Regular Version Controls */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-neutral-800 mb-4">Regular Version</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-6">
              {/* Center Dot Radius Control */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-neutral-700">
                  Center Dot Radius: {centerDotRadius}px
                </label>
                <input
                  type="range"
                  min="2"
                  max="100"
                  step="5"
                  value={centerDotRadius}
                  onChange={(e) => setCenterDotRadius(Number(e.target.value))}
                  className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer accent-neutral-600"
                />
                <input
                  type="number"
                  min="2"
                  max="100"
                  step="5"
                  value={centerDotRadius}
                  onChange={(e) => setCenterDotRadius(Number(e.target.value))}
                  className="px-3 py-1.5 text-sm border border-neutral-300 rounded-lg bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:border-transparent"
                />
              </div>

              {/* Ring Spacing Control */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-neutral-700">
                  Ring Spacing: {ringSpacing}px
                </label>
                <input
                  type="range"
                  min="2"
                  max="150"
                  step="5"
                  value={ringSpacing}
                  onChange={(e) => setRingSpacing(Number(e.target.value))}
                  className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer accent-neutral-600"
                />
                <input
                  type="number"
                  min="2"
                  max="150"
                  step="5"
                  value={ringSpacing}
                  onChange={(e) => setRingSpacing(Number(e.target.value))}
                  className="px-3 py-1.5 text-sm border border-neutral-300 rounded-lg bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:border-transparent"
                />
              </div>

              {/* Number of Rings Control */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-neutral-700">
                  Number of Rings: {numberOfRings}
                </label>
                <input
                  type="range"
                  min="2"
                  max="20"
                  step="1"
                  value={numberOfRings}
                  onChange={(e) => setNumberOfRings(Number(e.target.value))}
                  className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer accent-neutral-600"
                />
                <input
                  type="number"
                  min="2"
                  max="20"
                  step="1"
                  value={numberOfRings}
                  onChange={(e) => setNumberOfRings(Number(e.target.value))}
                  className="px-3 py-1.5 text-sm border border-neutral-300 rounded-lg bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:border-transparent"
                />
              </div>

              {/* Dots Per Ring Control */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-neutral-700">
                  Dots Per Ring: {dotsPerRing}
                </label>
                <input
                  type="range"
                  min="2"
                  max="30"
                  step="1"
                  value={dotsPerRing}
                  onChange={(e) => setDotsPerRing(Number(e.target.value))}
                  className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer accent-neutral-600"
                />
                <input
                  type="number"
                  min="2"
                  max="30"
                  step="1"
                  value={dotsPerRing}
                  onChange={(e) => setDotsPerRing(Number(e.target.value))}
                  className="px-3 py-1.5 text-sm border border-neutral-300 rounded-lg bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:border-transparent"
                />
              </div>

              {/* Use Outlines Checkbox */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-neutral-700">Use Outlines</label>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={useOutlines}
                    onChange={(e) => setUseOutlines(e.target.checked)}
                    className="w-4 h-4 rounded border-neutral-300 text-neutral-600 focus:ring-neutral-500"
                  />
                  <span className="text-sm text-neutral-600">
                    {useOutlines ? "Outlines" : "Fills"}
                  </span>
                </div>
              </div>

              {/* Dot Size Control */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-neutral-700">
                  Dot Size: {dotSize}px
                </label>
                <input
                  type="range"
                  min="2"
                  max="20"
                  step="1"
                  value={dotSize}
                  onChange={(e) => setDotSize(Number(e.target.value))}
                  className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer accent-neutral-600"
                />
                <input
                  type="number"
                  min="2"
                  max="20"
                  step="1"
                  value={dotSize}
                  onChange={(e) => setDotSize(Number(e.target.value))}
                  className="px-3 py-1.5 text-sm border border-neutral-300 rounded-lg bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:border-transparent"
                />
              </div>

              {/* Initial Ring Radius Control */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-neutral-700">
                  Initial Ring Radius: {initialRingRadius}px
                </label>
                <input
                  type="range"
                  min="2"
                  max="200"
                  step="5"
                  value={initialRingRadius}
                  onChange={(e) => setInitialRingRadius(Number(e.target.value))}
                  className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer accent-neutral-600"
                />
                <input
                  type="number"
                  min="2"
                  max="200"
                  step="5"
                  value={initialRingRadius}
                  onChange={(e) => setInitialRingRadius(Number(e.target.value))}
                  className="px-3 py-1.5 text-sm border border-neutral-300 rounded-lg bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Angled Version Controls */}
          <div className="mt-8">
            <h2 className="text-lg font-semibold text-neutral-800 mb-4">Angled Version</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-6">
              {/* Center Dot Radius Control */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-neutral-700">
                  Center Dot Radius: {angledCenterDotRadius}px
                </label>
                <input
                  type="range"
                  min="2"
                  max="100"
                  step="5"
                  value={angledCenterDotRadius}
                  onChange={(e) => setAngledCenterDotRadius(Number(e.target.value))}
                  className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer accent-neutral-600"
                />
                <input
                  type="number"
                  min="2"
                  max="100"
                  step="5"
                  value={angledCenterDotRadius}
                  onChange={(e) => setAngledCenterDotRadius(Number(e.target.value))}
                  className="px-3 py-1.5 text-sm border border-neutral-300 rounded-lg bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:border-transparent"
                />
              </div>

              {/* Ring Spacing Control */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-neutral-700">
                  Ring Spacing: {angledRingSpacing}px
                </label>
                <input
                  type="range"
                  min="2"
                  max="150"
                  step="5"
                  value={angledRingSpacing}
                  onChange={(e) => setAngledRingSpacing(Number(e.target.value))}
                  className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer accent-neutral-600"
                />
                <input
                  type="number"
                  min="2"
                  max="150"
                  step="5"
                  value={angledRingSpacing}
                  onChange={(e) => setAngledRingSpacing(Number(e.target.value))}
                  className="px-3 py-1.5 text-sm border border-neutral-300 rounded-lg bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:border-transparent"
                />
              </div>

              {/* Number of Rings Control */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-neutral-700">
                  Number of Rings: {angledNumberOfRings}
                </label>
                <input
                  type="range"
                  min="2"
                  max="20"
                  step="1"
                  value={angledNumberOfRings}
                  onChange={(e) => setAngledNumberOfRings(Number(e.target.value))}
                  className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer accent-neutral-600"
                />
                <input
                  type="number"
                  min="2"
                  max="20"
                  step="1"
                  value={angledNumberOfRings}
                  onChange={(e) => setAngledNumberOfRings(Number(e.target.value))}
                  className="px-3 py-1.5 text-sm border border-neutral-300 rounded-lg bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:border-transparent"
                />
              </div>

              {/* Dots Per Ring Control */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-neutral-700">
                  Dots Per Ring: {angledDotsPerRing}
                </label>
                <input
                  type="range"
                  min="2"
                  max="30"
                  step="1"
                  value={angledDotsPerRing}
                  onChange={(e) => setAngledDotsPerRing(Number(e.target.value))}
                  className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer accent-neutral-600"
                />
                <input
                  type="number"
                  min="2"
                  max="30"
                  step="1"
                  value={angledDotsPerRing}
                  onChange={(e) => setAngledDotsPerRing(Number(e.target.value))}
                  className="px-3 py-1.5 text-sm border border-neutral-300 rounded-lg bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:border-transparent"
                />
              </div>

              {/* Use Outlines Checkbox */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-neutral-700">Use Outlines</label>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={angledUseOutlines}
                    onChange={(e) => setAngledUseOutlines(e.target.checked)}
                    className="w-4 h-4 rounded border-neutral-300 text-neutral-600 focus:ring-neutral-500"
                  />
                  <span className="text-sm text-neutral-600">
                    {angledUseOutlines ? "Outlines" : "Fills"}
                  </span>
                </div>
              </div>

              {/* Dot Size Control */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-neutral-700">
                  Dot Size: {angledDotSize}px
                </label>
                <input
                  type="range"
                  min="2"
                  max="20"
                  step="1"
                  value={angledDotSize}
                  onChange={(e) => setAngledDotSize(Number(e.target.value))}
                  className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer accent-neutral-600"
                />
                <input
                  type="number"
                  min="2"
                  max="20"
                  step="1"
                  value={angledDotSize}
                  onChange={(e) => setAngledDotSize(Number(e.target.value))}
                  className="px-3 py-1.5 text-sm border border-neutral-300 rounded-lg bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:border-transparent"
                />
              </div>

              {/* Initial Ring Radius Control */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-neutral-700">
                  Initial Ring Radius: {angledInitialRingRadius}px
                </label>
                <input
                  type="range"
                  min="2"
                  max="200"
                  step="5"
                  value={angledInitialRingRadius}
                  onChange={(e) => setAngledInitialRingRadius(Number(e.target.value))}
                  className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer accent-neutral-600"
                />
                <input
                  type="number"
                  min="2"
                  max="200"
                  step="5"
                  value={angledInitialRingRadius}
                  onChange={(e) => setAngledInitialRingRadius(Number(e.target.value))}
                  className="px-3 py-1.5 text-sm border border-neutral-300 rounded-lg bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:border-transparent"
                />
              </div>

              {/* Vertical Offset Control */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-neutral-700">
                  Vertical Offset/Ring: {angledVerticalOffset}px
                </label>
                <input
                  type="range"
                  min="-50"
                  max="50"
                  step="1"
                  value={angledVerticalOffset}
                  onChange={(e) => setAngledVerticalOffset(Number(e.target.value))}
                  className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer accent-neutral-600"
                />
                <input
                  type="number"
                  min="-50"
                  max="50"
                  step="1"
                  value={angledVerticalOffset}
                  onChange={(e) => setAngledVerticalOffset(Number(e.target.value))}
                  className="px-3 py-1.5 text-sm border border-neutral-300 rounded-lg bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Regular Illustrations - Side by Side */}
      <div className="w-full flex gap-8 p-8">
        {/* Left - Light Theme */}
        <div className="theme-light flex-1">
          <SolarSystemIllustration
            centerDotRadius={centerDotRadius}
            ringSpacing={ringSpacing}
            numberOfRings={numberOfRings}
            dotsPerRing={dotsPerRing}
            useOutlines={useOutlines}
            dotSize={dotSize}
            initialRingRadius={initialRingRadius}
          />
        </div>

        {/* Right - Dark Theme */}
        <div className="theme-dark flex-1">
          <SolarSystemIllustration
            centerDotRadius={centerDotRadius}
            ringSpacing={ringSpacing}
            numberOfRings={numberOfRings}
            dotsPerRing={dotsPerRing}
            useOutlines={useOutlines}
            dotSize={dotSize}
            initialRingRadius={initialRingRadius}
          />
        </div>
      </div>

      {/* Angled Illustrations - Side by Side */}
      <div className="w-full flex gap-8 p-8">
        {/* Left - Light Theme */}
        <div className="theme-light flex-1">
          <AngledSolarSystem
            centerDotRadius={angledCenterDotRadius}
            ringSpacing={angledRingSpacing}
            numberOfRings={angledNumberOfRings}
            dotsPerRing={angledDotsPerRing}
            useOutlines={angledUseOutlines}
            dotSize={angledDotSize}
            initialRingRadius={angledInitialRingRadius}
            verticalOffsetPerRing={angledVerticalOffset}
          />
        </div>

        {/* Right - Dark Theme */}
        <div className="theme-dark flex-1">
          <AngledSolarSystem
            centerDotRadius={angledCenterDotRadius}
            ringSpacing={angledRingSpacing}
            numberOfRings={angledNumberOfRings}
            dotsPerRing={angledDotsPerRing}
            useOutlines={angledUseOutlines}
            dotSize={angledDotSize}
            initialRingRadius={angledInitialRingRadius}
            verticalOffsetPerRing={angledVerticalOffset}
          />
        </div>
      </div>
    </div>
  )
}

export default DemoPage
